import axios, { type AxiosError, type AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';
import { UAParser } from 'ua-parser-js';

import { type IMediator } from '#/core/mediator';

import { type IUrlParamsService, UrlParamsName } from '#/services/url-params';

import { RequestError } from '#/shared/exceptions/request-error';
import { SystemError } from '#/shared/exceptions/system-error';
import { isValidUrl } from '#/shared/utils/is-valid-url';

import { SERVICES_TYPES } from '#/services-types';

import { DeviceType, type IDeviceService } from '../device';
import { DataFeedClient, DataFeedEngine, type IEnvService } from '../env';
import { type ILogService } from '../log';

import { providerFactoryByEngine } from './api.model';
import { type IApiService } from './api.types';
import {
  type HistoryUrlParams,
  type HmrScheduleHistoryParams,
  type IApiProvider,
  type SymbolsUrlParams,
} from './providers';

export class ApiService implements IApiService {
  readonly #serviceName = 'ApiService';

  get #envService() {
    return this._mediator.get<IEnvService>(SERVICES_TYPES.EnvService);
  }
  get #logService() {
    return this._mediator.get<ILogService>(SERVICES_TYPES.LogService);
  }
  get #deviceService() {
    return this._mediator.get<IDeviceService>(SERVICES_TYPES.DeviceService);
  }
  get #urlParamsService() {
    return this._mediator.get<IUrlParamsService>(SERVICES_TYPES.UrlParamsService);
  }

  get #dataFeedClient() {
    const deviceType = this.#deviceService.deviceType;
    const isNativeApp = deviceType === DeviceType.AndroidNative || deviceType === DeviceType.IosNative;

    return isNativeApp ? DataFeedClient.Mobile : DataFeedClient.Web;
  }

  readonly #apiHost: string;

  #resolveApiHost() {
    const dataFeedHost = this.#envService.dataFeedHosts[this.#dataFeedClient][this.#dataFeedEngine];

    if (isValidUrl(dataFeedHost)) return dataFeedHost;

    const hostError = new SystemError('No valid data feed host configured for this client and engine.', {
      tags: {
        dataFeedClient: this.#dataFeedClient,
        dataFeedEngine: this.#dataFeedEngine,
      },
      dataFeedHost,
    });

    this.#logService.error(this.#serviceName, hostError);

    throw hostError;
  }

  readonly #client: AxiosInstance;

  get #clientApplicationAppId() {
    const appIdFromUrl = this.#urlParamsService.getParamByName(UrlParamsName.AppId);

    if (appIdFromUrl) {
      if (appIdFromUrl.startsWith('JM_CH_')) return appIdFromUrl;

      return `JM_CH_${appIdFromUrl}`;
    }

    if (this.#deviceService.deviceType === DeviceType.IosNative) {
      return 'JM_CH_IOS';
    }

    if (this.#deviceService.deviceType === DeviceType.AndroidNative) {
      return 'JM_CH_Android';
    }

    return 'JM_CH_UNKNOWN';
  }

  get #clientApplicationBrowser() {
    const browser = new UAParser(navigator.userAgent).getBrowser();
    const name = browser.name || 'Unknown';
    const version = browser.version || 'Unknown';

    return `${name} ${version}`;
  }

  get #clientApplicationHeaderValue() {
    return `${this.#clientApplicationAppId}/${this.#clientApplicationBrowser}/${window.APP_VERSION || 'Unknown'}`;
  }

  #createClient() {
    // The dev server proxies /api to the host in `x-target-proxy`, so there the requests stay on its own origin.
    const isDevelopmentMode = this.#envService.isDevelopmentMode;

    const client = axios.create({
      baseURL: isDevelopmentMode ? '' : this.#apiHost,
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': 'en',
        'Client-Application': this.#clientApplicationHeaderValue,
        ...(isDevelopmentMode && { 'x-target-proxy': this.#apiHost }),
      },
      withCredentials: false,
    });

    client.interceptors.response.use(
      (response) => response,
      async (requestError: AxiosError) => {
        if (requestError.response) {
          this.#logService.error(
            this.#serviceName,
            new RequestError('Request finished unsuccessful.', {
              tags: {
                statusCode: requestError.status,
              },
              requestError: {
                code: requestError.code,
                config: {
                  method: requestError.config?.method,
                  baseURL: requestError.config?.baseURL,
                  url: requestError.config?.url,
                  headers: requestError.config?.headers,
                  data: requestError.config?.data,
                  params: requestError.config?.params,
                },
                message: requestError.message,
              },
            })
          );
        }

        return Promise.reject(requestError);
      }
    );

    axiosRetry(client, {
      retryDelay: (retryCount) => retryCount * 2000,
      retryCondition: (error) => {
        if (!error.response) return true;

        if (error.response.status >= 500 || error.response.status === 429) return true;

        return false;
      },
      onRetry: (retryCount, error, requestConfig) => {
        this.#logService.warn(this.#serviceName, 'Retry call request.', {
          retryCount,
          requestConfig: {
            method: requestConfig.method,
            url: requestConfig.url,
            headers: requestConfig.headers,
            data: requestConfig.data,
          },
          errorMessage: error.message,
        });
      },
    });

    return client;
  }

  get #dataFeedEngine() {
    const providedEngine = this.#urlParamsService.getParamByName(UrlParamsName.Engine);

    // ? `in` also matches inherited Object.prototype keys (toString, constructor, ...); hasOwn does not.
    if (providedEngine && Object.hasOwn(providerFactoryByEngine, providedEngine)) {
      return providedEngine as DataFeedEngine;
    }

    return DataFeedEngine.Cfd;
  }

  readonly #provider: IApiProvider;

  #createProvider() {
    return providerFactoryByEngine[this.#dataFeedEngine](this.#client);
  }

  constructor(private readonly _mediator: IMediator) {
    this.#apiHost = this.#resolveApiHost();

    this.#client = this.#createClient();

    this.#provider = this.#createProvider();

    this.#logService.info(this.#serviceName, 'Instance was created.', {
      dataFeedClient: this.#dataFeedClient,
      dataFeedEngine: this.#dataFeedEngine,
      apiHost: this.#apiHost,
    });
  }

  async serverTime() {
    return this.#provider.serverTime();
  }

  async config() {
    return this.#provider.config();
  }

  async symbols({ urlParams }: { urlParams: SymbolsUrlParams }) {
    return this.#provider.symbols({ urlParams });
  }

  async candlesHistory({ urlParams }: { urlParams: HistoryUrlParams }) {
    return this.#provider.candlesHistory({ urlParams });
  }

  async ticksHistory({ urlParams }: { urlParams: HistoryUrlParams }) {
    return this.#provider.ticksHistory({ urlParams });
  }

  async hmrScheduleHistory({ urlParams }: { urlParams: HmrScheduleHistoryParams }) {
    return this.#provider.hmrScheduleHistory({ urlParams });
  }
}
